import { useCallback, useState } from 'react'
import { Dispatch } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { configApi } from '@redux/queries/config'
import { insurersApi } from '@redux/queries/insurers'
import { resetCtaInitialState } from '@redux/slices/ctaSlice'
import { resetFormsInitialState } from '@redux/slices/formsSlice'
import { resetWorkflowInitialState } from '@redux/slices/workflowSlice'
import { resetInsuranceMemberState } from '@redux/slices/insuranceMemberSlice'
import { resetClaimStatusInitialState } from '@redux/slices/claimStatusSlice'
import { resetPrescriptionFileInitialState } from '@redux/slices/prescriptionFileSlice'
import { overviewResetInitialState } from '@redux/slices/overviewWidgetSlice'
import { connectorOnLogin } from '@redux/slices/connectorSlice'
import { pushLocalAppEvent } from '@utils/localAppEventState'

export const useLoginEventHandler = (): (() => void) => {
  const dispatch = useDispatch()
  const [isInProgress, setIsInProgress] = useState(false)

  return useCallback(async () => {
    if (isInProgress) return

    setIsInProgress(true)

    try {
      // NOTE: most commerce FE will reload entire page on login
      // causing all redux state to be lost. Persisting the login
      // event to local storage allows us to detect the login at
      // a later time, e.g. on next auth token fetch.
      pushLocalAppEvent({ type: 'onLogin' })

      // Due to the page reload on login, we must keep everything
      // to a minimum. API calls will likely not work.

      resetState(dispatch)
    } catch (error) {
      console.debug('directbilling - error on login event handler', error)
    } finally {
      setIsInProgress(false)
    }
  }, [dispatch, isInProgress])
}

const resetState = (dispatch: Dispatch) => {
  dispatch(claimRecordsApi.util.resetApiState())
  dispatch(configApi.util.resetApiState())
  dispatch(insurersApi.util.resetApiState())

  dispatch(resetCtaInitialState())
  dispatch(resetFormsInitialState())
  dispatch(resetWorkflowInitialState())
  dispatch(resetInsuranceMemberState())
  dispatch(resetClaimStatusInitialState())
  dispatch(resetPrescriptionFileInitialState())
  dispatch(overviewResetInitialState())

  dispatch(connectorOnLogin())
}
